import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import {
	getUsersFromCollaborationGraph,
	getUsersWithProductAccess,
} from '@atlassian/cross-join-nudges';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';

import { showNudgeFlag } from '../ui/crossJoinNudgesFlags';

import {
	fireExperimentError,
	fireSiteOptedOutAnalytics,
	getExperimentAnalytics,
} from './analytics';
import { resolveCrossJoinNudges, CONF_TO_JIRA_INTEGRATION } from './resolve-cross-join-nudges';
import { increaseFeatureVisitedCount, isFeatureVisited } from './feature-visited';
import { setHasSeenNudgeInLast7Days } from './caching';
import {
	resolveJpdCrossJoinNudges,
	CONF_TO_JPD_INTEGRATION,
} from './resolve-jpd-cross-join-nudges';

export interface CrossJoinNudgesInput {
	cloudId: string;
	userId: string | null;
	flags: FlagsStateContainer;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	push: (url: string, forceReload?: boolean | undefined) => void;
}

// NOTE: This file is for old nudges - setup new files specifically per nudge experiment and add all helper functions there to avoid conflict
export const triggerJiraCrossJoinNudge = async ({
	cloudId,
	userId,
	flags,
	createAnalyticsEvent,
	push,
}: CrossJoinNudgesInput) => {
	const analytics = getExperimentAnalytics(CONF_TO_JIRA_INTEGRATION, createAnalyticsEvent);

	try {
		if (!userId || (await isFeatureVisited(CONF_TO_JIRA_INTEGRATION))) {
			return;
		}

		const response = await resolveCrossJoinNudges({ userId, cloudId });

		if (response.ineligibilityReason === null) {
			await showNudgeFlag({
				product: 'jira-software',
				integration: CONF_TO_JIRA_INTEGRATION,
				userId,
				cloudId,
				collaborators: response.collaborators,
				createAnalyticsEvent,
				flags,
				pushRoute: push,
			});
			increaseFeatureVisitedCount(CONF_TO_JIRA_INTEGRATION);

			// this is used in the eligibility check for loom nudge
			await setHasSeenNudgeInLast7Days();
		} else if (response.ineligibilityReason === 'SITE_OPTED_OUT') {
			fireSiteOptedOutAnalytics(analytics);
		}
	} catch (error: any) {
		fireExperimentError(analytics, error);
	}
};

type CrossJoinNudgesEligibilityInput = Pick<
	CrossJoinNudgesInput,
	'cloudId' | 'userId' | 'createAnalyticsEvent'
>;

export const checkJpdCrossJoinNudgeEligibility = async ({
	cloudId,
	userId,
	createAnalyticsEvent,
}: CrossJoinNudgesEligibilityInput): Promise<boolean> => {
	const analytics = getExperimentAnalytics(CONF_TO_JPD_INTEGRATION, createAnalyticsEvent);

	try {
		if (!userId) {
			return false;
		}

		if (await isFeatureVisited(CONF_TO_JPD_INTEGRATION)) {
			return false;
		}

		const eligibilityResponse = await resolveJpdCrossJoinNudges(cloudId, userId);

		if (!eligibilityResponse.eligible) {
			if (eligibilityResponse.ineligibilityReason === 'SITE_OPTED_OUT') {
				fireSiteOptedOutAnalytics(analytics);
			}
			return false;
		}

		return true;
	} catch (error: any) {
		fireExperimentError(analytics, error);
		return false;
	}
};

export const showJpdCrossJoinNudge = async ({
	cloudId,
	userId,
	flags,
	createAnalyticsEvent,
	push,
}: CrossJoinNudgesInput) => {
	const analytics = getExperimentAnalytics(CONF_TO_JPD_INTEGRATION, createAnalyticsEvent);

	try {
		if (!userId) {
			return;
		}

		const collaborators = await getUsersFromCollaborationGraph(
			CONF_TO_JPD_INTEGRATION,
			cloudId,
			userId,
		);
		const collaboratorsOnJpd = await getUsersWithProductAccess(
			'jira-product-discovery',
			cloudId,
			collaborators,
		);

		await showNudgeFlag({
			product: 'jira-product-discovery',
			integration: CONF_TO_JPD_INTEGRATION,
			userId,
			cloudId,
			collaborators: collaboratorsOnJpd,
			createAnalyticsEvent,
			flags,
			pushRoute: push,
		});
		increaseFeatureVisitedCount(CONF_TO_JPD_INTEGRATION);

		// this is used in the eligibility check for loom nudge
		await setHasSeenNudgeInLast7Days();
	} catch (error: any) {
		fireExperimentError(analytics, error);
	}
};
